import axios, { type AxiosError, type AxiosResponse } from "axios";
import type { CountryType, Nullable } from "../../@types/generic";
import { sendError } from "../../components/ErrorBoundary/helpers";
import { getWindow } from "../../helpers/getWindow";
import { localeMap } from "../../helpers/localeMap";

const headers: Record<string, string> = {
    token: process.env.NEXT_PUBLIC_API_TOKEN ?? "",
    "X-Application-ID": process.env.NEXT_PUBLIC_API_XAPPLICATIONID ?? "",
};

if (getWindow() !== undefined) {
    headers["Accept-Encoding"] = "gzip";
}

const axiosInstance = axios.create({
    baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
    headers,
    timeout: 5000,
});

const getAcceptLanguageHeader = (country: CountryType): string => {
    return localeMap[country];
};

export type Query = {
    path: string;
    params?: Record<
        string,
        Nullable<string | Array<string> | number | undefined>
    >;
};

type Config = {
    country: CountryType;
};

export const fetcher = async <T>(
    query: Query,
    config: Config
): Promise<AxiosResponse<T>> => {
    const paramsForQuery: Record<string, string> = {};
    Object.entries(query.params ?? {}).forEach(([key, value]) => {
        if (typeof value === "string") {
            paramsForQuery[key] = value;
        } else if (Array.isArray(value)) {
            paramsForQuery[key] = value.join(",");
        } else if (typeof value === "number") {
            paramsForQuery[key] = value.toString();
        }
    });

    let path = query.path;
    if (Object.keys(paramsForQuery).length > 0) {
        path = path.concat(
            `?${new URLSearchParams(paramsForQuery).toString()}`
        );
    }

    const acceptLanguageHeader = getAcceptLanguageHeader(config.country);

    return (
        axiosInstance
            .get<T>(path, {
                headers: {
                    "Accept-Language": acceptLanguageHeader,
                },
            })
            .then((response) => {
                if (response.status < 200 || response.status >= 300) {
                    throw new Error(
                        `${response.status.toString()} - ${path} - ${response.statusText}`
                    );
                }

                return response;
            })
            // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
            .catch((error: Error | AxiosError | unknown) => {
                if (isAxiosError(error)) {
                    // Access to config, request, and response
                    const errorData = {
                        "custom.axios.config": error.config
                            ? JSON.stringify(error.config)
                            : "",
                        "custom.axios.message": error.message,
                    };
                    if (error.response) {
                        // The request was made and the server responded with a status code
                        // that falls out of the range of 2xx
                        Object.assign(errorData, {
                            "custom.axios.data": error.response.data,
                            "custom.axios.status": JSON.stringify(
                                error.response.status
                            ),
                            "custom.axios.headers": error.response.headers,
                        });
                    } else if (error.request) {
                        // The request was made but no response was received
                        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                        // http.ClientRequest in node.js
                        const request = error.request as {
                            // XMLHttpRequest (browser)
                            responseURL?: string;
                            status?: number;
                            statusText?: string;
                            readyState?: number;
                            timeout?: number;
                            // http.ClientRequest (node.js)
                            method?: string;
                            protocol?: string;
                            host?: string;
                            path?: string;
                        };

                        Object.assign(errorData, {
                            "custom.axios.request.method": request.method ?? "",
                            "custom.axios.request.url":
                                request.responseURL ??
                                `${request.protocol ?? ""}//${request.host ?? ""}${request.path ?? ""}`,
                            "custom.axios.request.status":
                                request.status?.toString() ?? "",
                            "custom.axios.request.statusText":
                                request.statusText ?? "",
                            "custom.axios.request.readyState":
                                request.readyState?.toString() ?? "",
                            "custom.axios.request.timeout":
                                request.timeout?.toString() ?? "",
                        });
                    }

                    sendError(error.message, errorData);
                    return Promise.reject(error);
                } else if (error instanceof Error) {
                    sendError(error);
                    return Promise.reject(error);
                } else {
                    const newError = new Error(
                        `Unknown error: ${error?.toString() ?? ""}`
                    );
                    sendError(newError);
                    return Promise.reject(newError);
                }
            })
    );
};

export const isAxiosError = (error: unknown): error is AxiosError => {
    return axios.isAxiosError(error);
};
